import { useState, useCallback } from 'react';
import type {
  MultiselectProps,
  SelectProps,
} from '@amzn/awsui-components-console';

import type { CreateStringGroupFormData } from '../types';
import { isFormValid } from '../components/createStringGroupValidation';

/**
 * Fields whose validation visibility is tracked independently.
 *
 * `cxFamily` is owned by the modal (it only exists when the namespace has no
 * default family), so the modal marks it dirty itself via `markFieldDirty`.
 */
export type CreateStringGroupField =
  | 'name'
  | 'description'
  | 'teamName'
  | 'cxFamily'
  | 'cxIds'
  | 'sourceLocale';

interface UseCreateStringGroupFormReturn {
  // Field values
  name: string;
  description: string;
  teamName: string;
  selectedCxIds: MultiselectProps.Option[];
  sourceLocale: SelectProps.Option | null;

  // State flags
  submitting: boolean;
  submitError: string | null;
  showValidation: boolean;
  dirtyFields: ReadonlySet<CreateStringGroupField>;

  // Field setters
  setName: (value: string) => void;
  setDescription: (value: string) => void;
  setTeamName: (value: string) => void;
  setSelectedCxIds: (options: MultiselectProps.Option[]) => void;
  setSourceLocale: (option: SelectProps.Option | null) => void;
  setSubmitError: (error: string | null) => void;

  // Validation visibility
  markFieldDirty: (field: CreateStringGroupField) => void;
  clearFieldDirty: (...fields: CreateStringGroupField[]) => void;
  shouldShowValidation: (field: CreateStringGroupField) => boolean;

  // Actions
  resetForm: () => void;
  handleSubmit: (
    onSubmit: (data: CreateStringGroupFormData) => Promise<void>,
    onSuccess: () => void,
    cxFamilyId: string,
  ) => Promise<void>;
}

/**
 * Encapsulates all form state, validation, and submission logic for the
 * Create String Group modal.
 *
 * Validation visibility follows a "validate on dirty" model: a field's error is
 * shown as soon as the user has modified that field, and every field's error is
 * shown once Submit has been pressed (`showValidation`). Untouched fields stay
 * quiet until submit, so an empty form never opens covered in errors.
 */
export function useCreateStringGroupForm(): UseCreateStringGroupFormReturn {
  const [name, setNameValue] = useState('');
  const [description, setDescriptionValue] = useState('');
  const [teamName, setTeamNameValue] = useState('');
  const [selectedCxIds, setSelectedCxIdsValue] = useState<
    MultiselectProps.Option[]
  >([]);
  const [sourceLocale, setSourceLocaleValue] =
    useState<SelectProps.Option | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<CreateStringGroupField>>(
    () => new Set(),
  );

  const markFieldDirty = useCallback((field: CreateStringGroupField) => {
    setDirtyFields((previous) => {
      if (previous.has(field)) return previous;
      const next = new Set(previous);
      next.add(field);
      return next;
    });
  }, []);

  /**
   * Makes fields quiet again (until re-touched or Submit is pressed).
   *
   * Needed when a field's value is reset programmatically as a *consequence* of
   * another field changing — e.g. switching CX family clears the CX IDs and
   * source locale. Without this, a field the user had already touched would keep
   * its dirty flag and immediately flash a "required" error for a value the user
   * did not clear themselves.
   */
  const clearFieldDirty = useCallback((...fields: CreateStringGroupField[]) => {
    setDirtyFields((previous) => {
      if (!fields.some((field) => previous.has(field))) return previous;
      const next = new Set(previous);
      fields.forEach((field) => next.delete(field));
      return next;
    });
  }, []);

  const shouldShowValidation = useCallback(
    (field: CreateStringGroupField) => showValidation || dirtyFields.has(field),
    [showValidation, dirtyFields],
  );

  // ─── Setters: changing a field marks it dirty ────────────────────────────
  const setName = useCallback(
    (value: string) => {
      setNameValue(value);
      markFieldDirty('name');
    },
    [markFieldDirty],
  );

  const setDescription = useCallback(
    (value: string) => {
      setDescriptionValue(value);
      markFieldDirty('description');
    },
    [markFieldDirty],
  );

  const setTeamName = useCallback(
    (value: string) => {
      setTeamNameValue(value);
      markFieldDirty('teamName');
    },
    [markFieldDirty],
  );

  /**
   * Only a non-empty selection marks the field dirty: the modal also calls this
   * with `[]` to clear dependent state when the CX family changes, which is not
   * a user edit of this field.
   */
  const setSelectedCxIds = useCallback(
    (options: MultiselectProps.Option[]) => {
      setSelectedCxIdsValue(options);
      if (options.length > 0) {
        markFieldDirty('cxIds');
      }
    },
    [markFieldDirty],
  );

  /**
   * Only a real selection marks the field dirty — the modal resets this to
   * `null` whenever the family or the CX IDs change, which is not a user edit.
   */
  const setSourceLocale = useCallback(
    (option: SelectProps.Option | null) => {
      setSourceLocaleValue(option);
      if (option !== null) {
        markFieldDirty('sourceLocale');
      }
    },
    [markFieldDirty],
  );

  const resetForm = useCallback(() => {
    setNameValue('');
    setDescriptionValue('');
    setTeamNameValue('');
    setSelectedCxIdsValue([]);
    setSourceLocaleValue(null);
    setSubmitting(false);
    setSubmitError(null);
    setShowValidation(false);
    setDirtyFields(new Set());
  }, []);

  const handleSubmit = useCallback(
    async (
      onSubmit: (data: CreateStringGroupFormData) => Promise<void>,
      onSuccess: () => void,
      cxFamilyId: string,
    ) => {
      setShowValidation(true);
      setSubmitError(null);

      const formData: CreateStringGroupFormData = {
        name: name.trim(),
        description: description.trim(),
        teamName: teamName.trim(),
        cxFamilyId,
        cxIds: selectedCxIds,
        sourceLocale,
      };

      if (!isFormValid(formData)) {
        return;
      }

      setSubmitting(true);
      try {
        await onSubmit(formData);
        resetForm();
        onSuccess();
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : 'Failed to create string group. Please try again.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [name, description, teamName, selectedCxIds, sourceLocale, resetForm],
  );

  return {
    name,
    description,
    teamName,
    selectedCxIds,
    sourceLocale,
    submitting,
    submitError,
    showValidation,
    dirtyFields,
    setName,
    setDescription,
    setTeamName,
    setSelectedCxIds,
    setSourceLocale,
    setSubmitError,
    markFieldDirty,
    clearFieldDirty,
    shouldShowValidation,
    resetForm,
    handleSubmit,
  };
}
